import {
	AsyncSelect,
	Box,
	Button,
	CheckboxInput,
	ContentToggle,
	Divider,
	Dropdown,
	Icons,
	Inline,
	joinAttributes,
	Select,
	Spinner,
	Stack,
	Table,
	TableDataCell,
	TableHeaderDataCell,
	Text,
	Time,
	useBreakpoints,
} from "@sembark-travel/ui-base"
import { useCallback, useEffect, useRef, useState } from "react"
import { TTravelActivity, TTravelActivityTicketType } from "./store"
import { AddActivityInDialog } from "./NewItem"
import {
	ICursorListResponse,
	IListResponse,
	ISimpleListResponse,
	XHRInstance,
	useXHR,
} from "@sembark-travel/xhr"
import { ButtonLink, Link, useLocationQuery } from "@sembark-travel/ui-router"
import {
	CursorListView,
	Search,
	TSearchParams,
	areAdvancedFiltersAppliedDefault,
	useSearch,
} from "@sembark-travel/ui-list"
import { SelectTripDestination, TTripDestination } from "../TripDestinations"
import {
	DateRangePickerField,
	Form,
	SelectField,
	SubmissionError,
	SwitchInputField,
	TextInputField,
	isTruthy,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { generatePath } from "../router-utils"
import { PERMISSIONS, useCheckPermissions, useHasFeatureFlag } from "../Auth"
import { TemporaryStorage } from "../DownloadManager"
import {
	dateToUTCString,
	endOf,
	formatDate,
	startOf,
} from "@sembark-travel/datetime-utils"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import * as Validator from "yup"
import React from "react"
import { IUser, SelectUsers } from "../Users"
import useSWR from "swr"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { checkFilterAppliedFn } from "../utils"
import { EntryTypeIcon } from "../shared/EntryTypeConfig"

function XHR(xhr: XHRInstance) {
	return {
		async get(params?: unknown) {
			return xhr
				.get<ISimpleListResponse<TTravelActivity>>("/travel-activities", {
					params,
				})
				.then((resp) => resp.data)
		},
		async getWithPagination(params?: Record<string, unknown>) {
			return xhr
				.get<ICursorListResponse<TTravelActivity>>("/travel-activities", {
					params: {
						pagination: "cursor",
						...params,
					},
				})
				.then((resp) => resp.data)
		},
		async getWithPageNumber(params?: Record<string, unknown>) {
			return xhr
				.get<IListResponse<TTravelActivity>>("/travel-activities", {
					params: {
						pagination: "paginate",
						...params,
					},
				})
				.then((resp) => resp.data)
		},
	}
}

type TFilters = TSearchParams & {
	rate_expire_after?: string | Date
	rate_expire_before?: string | Date
	last_updated_after?: string | Date
	last_updated_before?: string | Date
	disabled_only?: 1 | 0
	trip_destinations?: Array<TTripDestination>
	last_updated_by?: Array<IUser>
	entry_type?: Array<{ name: string; id: string }>
}
function filtersToAPIParams(params: TFilters) {
	const { entry_type, ...restData } = params
	return {
		...restData,
		rate_expire_after: params.rate_expire_after
			? dateToUTCString(startOf(params.rate_expire_after, "day"))
			: null,
		rate_expire_before: params.rate_expire_before
			? dateToUTCString(endOf(params.rate_expire_before, "day"))
			: null,
		rate_expire_after_local: params.rate_expire_after
			? formatDate(startOf(params.rate_expire_after, "day"), "YYYY-MM-DD")
			: null,
		rate_expire_before_local: params.rate_expire_before
			? formatDate(endOf(params.rate_expire_before, "day"), "YYYY-MM-DD")
			: null,
		last_updated_after: params.last_updated_after
			? dateToUTCString(startOf(params.last_updated_after, "day"))
			: null,
		last_updated_before: params.last_updated_before
			? dateToUTCString(endOf(params.last_updated_before, "day"))
			: null,
		disabled_only: isTruthy(params.disabled_only) ? 1 : null,
		trip_destinations: params.trip_destinations?.map((t) => t.id),
		last_updated_by: params.last_updated_by?.map((t) => t.id),
		entry_type: entry_type?.map((t) => t.id),
	}
}

function Filters() {
	const hasPreload = useHasFeatureFlag("ai_generated_content")
	const entryOptions = [
		{
			name: "Added by sheet only",
			id: "only_sheet",
		},
		{
			name: "Added manual only",
			id: "only_manual",
		},
		hasPreload
			? {
					name: "Added by AI only",
					id: "only_preload",
				}
			: null,
		{
			name: `Mix-by manual, sheet ${hasPreload ? "and AI" : ""}`,
			id: "only_mixed",
		},
	].filter(Boolean)

	return (
		<Stack gap="4">
			<SelectField
				select={SelectTripDestination}
				name="trip_destinations"
				label="Destinations"
				multiple
				fetchOnMount
			/>
			<SelectField
				select={Select}
				label="Upload Method"
				name="entry_type"
				multiple
				options={entryOptions}
			/>
			<DateRangePickerField
				label="Rate Validity"
				fromName="rate_expire_after"
				toName="rate_expire_before"
			/>
			<SelectField
				select={SelectUsers}
				name="last_updated_by"
				label="Last Updated By"
				fetchOnMount
				multiple
			/>
			<DateRangePickerField
				label="Last Updated Between"
				fromName="last_updated_after"
				toName="last_updated_before"
			/>
			<SwitchInputField name="disabled_only" label="Disabled Only" />
		</Stack>
	)
}

type ITravelActivityTicketType = TTravelActivityTicketType

export function TravelActivitiesList() {
	const [query, setQuery] = useLocationQuery()
	const [params, setParams] = useSearch<TFilters>(query)
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	const { hasPermission } = useCheckPermissions()
	const [componentKey, setComponentKey] = useState(0)
	const { xs, sm } = useBreakpoints()

	const getDefaultValues = useCallback((data: TTravelActivity) => {
		return [
			{
				updated_by: data.updated_by,
				created_by: data.created_by,
				created_at: data.created_at,
				updated_at: data.updated_at,
				entry_type: data.entry_type,
				id: 1,
				name: "",
				activity_id: data.id,
				display_name: "",
				rate_expiry_date_local: data.rate_expiry_date_local,
				rate_expiry_date: data.rate_expiry_date,
				rate_uploaded_by: data.rate_uploaded_by,
			},
		]
	}, [])
	return (
		<Search
			initialParams={params}
			resetParams={{ q: "" }}
			onSearch={(params) => {
				setParams({ ...params, cursor: null })
			}}
			title="Travel Activities"
			Filters={Filters}
			areAdvancedFiltersApplied={(params) => {
				const { disabled_only, ...otherParams } = params
				return (
					isTruthy(disabled_only) ||
					areAdvancedFiltersAppliedDefault(otherParams)
				)
			}}
			actions={({ changeAdvancedFiltersVisibility }) => (
				<Inline gap="4">
					<ButtonLink to={generatePath("/travel-activity-prices")}>
						Prices
					</ButtonLink>
					{hasPermission(PERMISSIONS.CREATE_TRANSPORT_SERVICES) ? (
						<ButtonLink
							to={generatePath("/travel-activities/new")}
							level="secondary"
						>
							Add New
						</ButtonLink>
					) : null}
					<Dropdown alignRight>
						<Dropdown.ToggleButton level="tertiary">
							<Icons.DotsVertical />
						</Dropdown.ToggleButton>
						<Dropdown.Menu>
							{hasPermission(PERMISSIONS.UPLOAD_BULK_TRANSPORT_SERVICES) ? (
								<>
									<TemporaryStorage
										initUrl={"/travel-activities/download"}
										initParams={{
											...filtersToAPIParams(params),
										}}
									>
										{({ generate, url, message, reset }) => (
											<Dropdown.MenuItem
												onClick={!url ? generate : () => reset()}
												href={url || "#"}
												target={url ? "_blank" : undefined}
											>
												{message || (
													<>
														<Icons.DocumentDownload /> Download Travel
														Activities Details
													</>
												)}
											</Dropdown.MenuItem>
										)}
									</TemporaryStorage>
									{hasPermission(PERMISSIONS.CREATE_TRANSPORT_SERVICES) ? (
										<DisableActivitiesDialog
											onSuccess={() => {
												setComponentKey((key) => ++key)
											}}
											params={params}
											key={componentKey}
											FilterOpen={() => changeAdvancedFiltersVisibility(true)}
										>
											{({ onDisable }) => (
												<Dropdown.MenuItem onClick={onDisable}>
													<Icons.Ban /> Bulk Disable Travel {xs ? <br /> : null}{" "}
													Activities
												</Dropdown.MenuItem>
											)}
										</DisableActivitiesDialog>
									) : null}
								</>
							) : null}
						</Dropdown.Menu>
					</Dropdown>
				</Inline>
			)}
		>
			<CursorListView<TTravelActivity, TFilters>
				pageKey="/travel-activities-list-view"
				params={params}
				fetch={(xhr, params) =>
					XHR(xhr).getWithPagination({
						...filtersToAPIParams(params),
						disabled_only: isTruthy(params.disabled_only) ? 1 : null,
						limit: 15,
						include:
							"thumbs,images,thumb_image_url,updated_by,created_by,rate_uploaded_by",
					})
				}
				onCursorChange={(cursor) => setParams({ ...params, cursor })}
				key={componentKey}
			>
				{({ items: travelActivity }) =>
					!xs && !sm ? (
						<Table responsive bordered hover>
							<thead>
								<tr>
									<TableHeaderDataCell></TableHeaderDataCell>
									<TableHeaderDataCell style={{ maxWidth: "300px" }}>
										Activities Name
									</TableHeaderDataCell>
									<TableHeaderDataCell textAlign="center">
										<Inline alignItems={"end"} justifyContent="between">
											<Box textAlign={"left"} style={{ width: "50%" }}>
												Ticket/Packages
											</Box>
											<Box
												wordBreak="words"
												whiteSpace="normal"
												style={{ width: "10%" }}
												textAlign={"center"}
											>
												Entry Method
											</Box>
											<Box
												wordBreak="words"
												whiteSpace="normal"
												style={{ width: "20%" }}
												textAlign={"center"}
											>
												Rate Validity
											</Box>
											<Box
												wordBreak="words"
												whiteSpace="normal"
												style={{ width: "20%" }}
												textAlign={"center"}
											>
												Last Updated
											</Box>
										</Inline>
									</TableHeaderDataCell>
								</tr>
							</thead>
							<tbody>
								{travelActivity.map(
									({
										id,
										ticket_types,
										media_url,
										name,
										deleted_at,
										...restData
									}) => (
										<tr>
											<TableDataCell style={{ maxWidth: "100px" }}>
												{media_url ? (
													<Box
														as="img"
														src={media_url}
														alt="Display"
														borderWidth="1"
														rounded="md"
													/>
												) : (
													<Icons.TravelActivitie color={"accent"} size="8" />
												)}
											</TableDataCell>
											<TableDataCell style={{ maxWidth: "300px" }}>
												<Link
													fontSize="md"
													to={id.toString()}
													color="accent"
													anchored
													fontWeight="semibold"
												>
													<Inline
														width={"full"}
														alignItems={"center"}
														gap={"1"}
													>
														<Text wordBreak="words">{name}</Text>
														<Box>
															{deleted_at ? (
																<Icons.Ban color={"danger"} />
															) : null}
														</Box>
													</Inline>
												</Link>
											</TableDataCell>
											<TableDataCell style={{ width: "60%" }}>
												<Box borderTopWidth={"1"} borderTopColor={"emphasis"}>
													<ContentToggle
														gap={"0"}
														renderItem={(s: ITravelActivityTicketType) => (
															<Inline
																key={s.id}
																flexWrap="wrap"
																alignItems="stretch"
																borderBottomWidth="1"
																borderBottomColor="emphasis"
															>
																<Box
																	padding={"2"}
																	style={{
																		width: "50%",
																	}}
																	borderLeftWidth={"1"}
																	borderBottomColor="emphasis"
																>
																	<Inline
																		alignItems={"center"}
																		justifyContent={s.name ? "start" : "center"}
																		gap={"1"}
																	>
																		<Text>{s.name || "-"}</Text>
																		{s.deleted_at ? (
																			<Box>
																				<Icons.Ban color={"danger"} />
																			</Box>
																		) : null}
																	</Inline>
																</Box>
																<Box
																	textAlign="center"
																	padding={"2"}
																	style={{
																		width: "10%",
																	}}
																	borderBottomColor="emphasis"
																	borderLeftWidth={"1"}
																>
																	<EntryTypeIcon entryType={s.entry_type} />
																</Box>
																<Box
																	textAlign="center"
																	wordBreak="words"
																	whiteSpace="normal"
																	padding={"2"}
																	style={{
																		width: "20%",
																	}}
																	borderLeftWidth={"1"}
																	borderBottomColor="emphasis"
																>
																	<Inline>
																		<Time
																			timestamp={
																				s.rate_expiry_date
																					? endOf(
																							s.rate_expiry_date,
																							"days"
																						).toUTCString()
																					: undefined
																			}
																			localTimestamp={
																				s.rate_expiry_date_local
																					? endOf(
																							s.rate_expiry_date_local,
																							"days"
																						).toString()
																					: undefined
																			}
																			format="DD MMM, YYYY"
																			wordBreak="words"
																			whiteSpace="normal"
																			textAlign={"left"}
																		/>
																	</Inline>
																	{s.rate_uploaded_by?.name ? (
																		<Inline alignItems={"center"}>
																			<Box>
																				<Icons.User />
																			</Box>
																			<Text
																				wordBreak="words"
																				whiteSpace="normal"
																				color={"muted"}
																				textAlign={"left"}
																			>
																				{s.rate_uploaded_by?.name}
																			</Text>
																		</Inline>
																	) : (
																		<Box>-</Box>
																	)}
																</Box>
																<Box
																	wordBreak="words"
																	whiteSpace="normal"
																	padding={"2"}
																	style={{
																		width: "20%",
																	}}
																	borderLeftWidth={"1"}
																	borderRightWidth={"1"}
																	borderBottomColor="emphasis"
																	textAlign="center"
																>
																	<Inline>
																		<Time
																			timestamp={s.updated_at || s.created_at}
																			format="DD MMM, YYYY"
																			wordBreak="words"
																			whiteSpace="normal"
																			textAlign={"left"}
																		/>
																	</Inline>
																	<Inline alignItems={"center"}>
																		<Box>
																			<Icons.User />
																		</Box>
																		<Text
																			wordBreak="words"
																			whiteSpace="normal"
																			color={"muted"}
																			textAlign={"left"}
																		>
																			{s.updated_by?.name || s.created_by?.name}
																		</Text>
																	</Inline>
																</Box>
															</Inline>
														)}
														data={
															ticket_types.length
																? ticket_types
																: getDefaultValues({
																		id,
																		...restData,
																	} as TTravelActivity)
														}
													/>
												</Box>
											</TableDataCell>
										</tr>
									)
								)}
							</tbody>
						</Table>
					) : (
						<Box as="ol">
							{travelActivity.map(
								({ id, ticket_types, media_url, name, ...restData }) => (
									<Box
										as="li"
										key={id}
										padding="4"
										borderWidth="1"
										rounded="lg"
										marginBottom="4"
										bgColor="subtle"
									>
										<Box marginBottom="4">
											<Link
												fontSize="md"
												to={id.toString()}
												color="accent"
												anchored
												fontWeight="semibold"
											>
												<Inline gap="4">
													{media_url ? (
														<Box
															as="img"
															src={media_url}
															alt="Display"
															rounded="md"
															borderWidth="1"
															style={{ width: "100px", minWidth: "100px" }}
														/>
													) : null}
													<Box>{name}</Box>
												</Inline>
											</Link>
										</Box>
										<ContentToggle
											renderItem={(s: ITravelActivityTicketType) => (
												<>
													<Text
														textAlign={"left"}
														fontWeight="semibold"
														fontSize={"md"}
													>
														{s.name}
													</Text>
													<Inline flexWrap={"wrap"}>
														{joinAttributes(
															<Box>
																{s.entry_type === "manually"
																	? "Manually"
																	: s.entry_type === "by_preload"
																		? "By AI"
																		: "By Sheet"}
															</Box>,

															s.rate_expiry_date ? (
																<Box>
																	rate expiry on :{" "}
																	<Time
																		timestamp={s?.rate_expiry_date ?? undefined}
																		localTimestamp={
																			s.rate_expiry_date_local ?? undefined
																		}
																		format="DD MMM, YYYY"
																		whiteSpace="nowrap"
																		wordBreak="words"
																		color={"muted"}
																	/>
																</Box>
															) : null,
															s.rate_uploaded_by ? (
																<Inline gap={"1"}>
																	rate expiry by :{" "}
																	<Text
																		wordBreak="words"
																		whiteSpace="normal"
																		color={"muted"}
																	>
																		{s.rate_uploaded_by?.name ?? "-"}
																	</Text>
																</Inline>
															) : null,

															<Inline gap={"1"}>
																last updated by :{" "}
																<Text
																	wordBreak="words"
																	whiteSpace="normal"
																	color={"muted"}
																>
																	{s?.updated_by?.name || s?.created_by?.name}
																</Text>
															</Inline>,

															<Box>
																last updated on :{" "}
																<Time
																	timestamp={s.updated_at || s.created_at}
																	localTimestamp={
																		s.updated_at ?? s.created_at ?? undefined
																	}
																	format="DD MMM, YYYY"
																	wordBreak="words"
																	whiteSpace="normal"
																	color={"muted"}
																/>
															</Box>
														)}
													</Inline>
													<Divider sm />
												</>
											)}
											data={
												ticket_types.length
													? ticket_types
													: getDefaultValues({
															id,
															...restData,
														} as TTravelActivity)
											}
										/>
									</Box>
								)
							)}
						</Box>
					)
				}
			</CursorListView>
		</Search>
	)
}

export function SelectActivity({
	tripDestinations,
	creatable,
	...props
}: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch"> & {
	tripDestinations?: Array<TTripDestination>
}) {
	const xhr = useXHR()
	const promiseOfCreation = useRef<
		((activity: TTravelActivity) => void) | undefined
	>(undefined)
	const [initialName, setInitialName] = useState<string>("")
	const { hasPermission } = useCheckPermissions()
	const canCreateNewActivityService =
		creatable && hasPermission(PERMISSIONS.CREATE_TRANSPORT_SERVICES)
	return (
		<>
			<AsyncSelect
				creatable={canCreateNewActivityService}
				{...props}
				perFetchLimit={15}
				fetch={async (q, { page }) => {
					return XHR(xhr)
						.get({
							limit: 15,
							page,
							q,
							trip_destinations: tripDestinations?.length
								? tripDestinations.map((t) => t.id)
								: undefined,
							pagination: "simple",
							include: "created_by,trip_destinations",
						})
						.then((resp) => resp.data)
				}}
				onCreateNew={(q) => {
					setInitialName(q)
					return new Promise((resolve) => {
						promiseOfCreation.current = resolve
					})
				}}
			/>
			<AddActivityInDialog
				open={Boolean(initialName)}
				onClose={() => setInitialName("")}
				initialValues={{
					name: initialName,
					trip_destinations: tripDestinations,
				}}
				onSuccess={(activity) => {
					promiseOfCreation.current?.(activity)
				}}
			/>
		</>
	)
}

interface RefHandle {
	getUnselectedIds: () => number[]
	isItemZero: boolean
}

export function DisableActivitiesDialog({
	children,
	params,
	onSuccess,
	FilterOpen,
}: {
	children: (props: { onDisable: () => void }) => React.ReactNode
	params: TFilters
	onSuccess: () => void
	FilterOpen: () => void
}) {
	const [isOpen, openDialog, closeDialog] = useDialog()
	const [count, setCount] = useState({
		count: 0,
		totalCount: 0,
	})
	const xhr = useXHR()
	const childRef = useRef<RefHandle>(null)
	const inProgressRef = useRef(false)
	const [inProgressState, setInProgressState] = useState(false)
	const [isSelectAll, setIsSelectAll] = useState(false)

	useEffect(() => {
		if (!inProgressRef.current && inProgressState) {
			showSnackbar("Disabling travel activities...")
		}
		if (inProgressRef.current && !inProgressState) {
			showSnackbar("Travel activities disabled successfully!")
			closeDialog()
		}
		inProgressRef.current = inProgressState
	}, [inProgressState, closeDialog])
	useEffect(() => {
		async function getCount() {
			try {
				const item = await XHR(xhr).getWithPageNumber({
					...filtersToAPIParams(params),
					limit: 1,
					page: 1,
					pagination: "paginate",
				})
				setCount({
					count: 0,
					totalCount: item.meta.total || 0,
				})
			} catch (e) {
				const error = e as Error
				window.alert(error.message)
			}
		}
		if (isOpen && checkFilterAppliedFn(params)) {
			getCount()
		}
		if (!isOpen) {
			setCount({
				count: 0,
				totalCount: 0,
			})
			setIsSelectAll(false)
		}
	}, [isOpen, params, xhr])

	const decreaseIncrease = (type: "decrease" | "increase") => {
		setCount((val) => ({
			...val,
			count: type === "decrease" ? --val.count : ++val.count,
		}))
	}
	const openFilter = () => {
		closeDialog()
		FilterOpen()
	}
	if (isTruthy(params.disabled_only)) return

	return (
		<Batch
			onFinish={() => {
				closeDialog()
				onSuccess()
			}}
		>
			{({ init, xhr, inProgress }) => {
				if (inProgress !== inProgressState) {
					setInProgressState(inProgress)
				}
				return (
					<>
						{children({ onDisable: () => openDialog() })}
						<Dialog
							open={isOpen}
							onClose={closeDialog}
							title="Bulk Disable Travel activities"
							lg
						>
							<Form<{ confirmed_activities_name: number }>
								validate={validateFormValues(
									Validator.object().shape({
										confirmed_activities_name: Validator.number()
											.equals(
												[count.count],
												`Please type ${count.count} to confirm the disabling`
											)
											.not([0], "Please type a valid number")
											.required(
												"Please type the count to confirm the disabling"
											),
									})
								)}
								onSubmit={withServerErrors(async () => {
									const resp = await xhr.delete(
										`/travel-activities/bulk-disable`,
										{
											data: {
												...filtersToAPIParams(params),
												...(isSelectAll
													? {
															excluded_ids: childRef.current
																?.getUnselectedIds()
																?.map((id) => id),
														}
													: {
															included_ids: childRef.current
																?.getUnselectedIds()
																?.map((id) => id),
														}),
											},
										}
									)
									init(resp.data.data)
								})}
								subscription={{ submitting: true }}
							>
								{({ submitting, handleSubmit }) => (
									<form noValidate onSubmit={handleSubmit}>
										<Dialog.Body padding="8">
											<Inline justifyContent={"between"} gap={"2"}>
												<Text fontSize="lg" fontWeight="semibold">
													Please review details before disable
												</Text>
												{!isSelectAll &&
												checkFilterAppliedFn(params) &&
												!childRef.current?.isItemZero ? (
													<Inline
														cursor="pointer"
														as="label"
														alignItems="center"
														gap="2"
													>
														<CheckboxInput
															checked={isSelectAll}
															onChange={(e) => {
																setIsSelectAll(true)
																setCount((val) => {
																	return { ...val, count: val.totalCount }
																})
															}}
															name="selected_all"
															id="selected_all"
														/>
														<Box htmlFor="selected_all" as="label">
															Select All
														</Box>
													</Inline>
												) : null}
											</Inline>
											<InfiniteScroll
												decreaseIncrease={decreaseIncrease}
												ref={childRef}
												params={params}
												openFilter={openFilter}
												isSelectAll={isSelectAll}
											/>
											{checkFilterAppliedFn(params) &&
											!childRef.current?.isItemZero ? (
												<>
													<Text color="primary">
														<Icons.Info /> Disabling these activities is not
														permanent. You can restore them at any time.
													</Text>

													{count.count ? (
														<Box marginTop="8">
															<TextInputField
																label={`Please Type ${count.count} to confirm`}
																name="confirmed_activities_name"
																type="number"
																aria-label="Type in the number of the activities to confirm that you want to disable these activities"
																required
															/>
														</Box>
													) : null}
												</>
											) : null}
										</Dialog.Body>
										{checkFilterAppliedFn(params) &&
										!childRef.current?.isItemZero ? (
											<Dialog.Footer>
												<SubmissionError />
												<Button
													disabled={
														submitting ||
														(inProgressRef.current && !inProgressState)
													}
													type="submit"
													status="danger"
													level="primary"
												>
													{inProgressRef.current && !inProgressState ? (
														"Disabling..."
													) : (
														<>
															<Icons.Ok /> I understand, disable these
															activities
														</>
													)}
												</Button>
												<Button
													disabled={submitting}
													onClick={() => closeDialog()}
													level="tertiary"
												>
													Cancel
												</Button>
											</Dialog.Footer>
										) : null}
									</form>
								)}
							</Form>
						</Dialog>
					</>
				)
			}}
		</Batch>
	)
}

type CursorMeta = ICursorListResponse<unknown>["meta"]
type TInfiniteScrollBase = {
	params: TFilters
	decreaseIncrease: (type: "decrease" | "increase") => void
	openFilter: () => void
	isSelectAll: boolean
}

function InfiniteScrollBase(
	{ params, decreaseIncrease, openFilter, isSelectAll }: TInfiniteScrollBase,

	ref: React.ForwardedRef<RefHandle>
) {
	const [items, setItems] = useState<TTravelActivity[]>([])
	const [selectedItmes, setSelectedItmes] = useState<number[]>([])
	type Status = {
		isLoading: boolean
		error: string
	}
	const [status, setStatus] = useState<Status>({
		isLoading: false,
		error: "",
	})

	const groupRef = useRef<HTMLDivElement>(null)
	const isFetchingRef = useRef(false)
	const pageInfo = useRef<CursorMeta>()
	const SCROLL_THRESHOLD = 100
	const xhr = useXHR()

	const getActivitiesLocations = useCallback(async () => {
		if (isFetchingRef.current) return
		setStatus((val) => ({ ...val, isLoading: true }))
		isFetchingRef.current = true
		try {
			const item = await XHR(xhr).getWithPagination({
				...filtersToAPIParams(params),
				disabled_only: isTruthy(params.disabled_only) ? 1 : null,
				limit: 15,
				cursor: pageInfo.current?.next_cursor,
			})
			pageInfo.current = item.meta

			setItems((prev) =>
				pageInfo.current?.prev_cursor ? [...prev, ...item.data] : [...item.data]
			)
		} catch (e) {
			const error = e as Error
			setStatus((val) => ({
				...val,
				error: error.message || "Something went wrong",
			}))
		} finally {
			isFetchingRef.current = false
			setStatus((val) => ({ error: "", isLoading: false }))
		}
	}, [xhr, params])
	useEffect(() => {
		if (checkFilterAppliedFn(params)) {
			getActivitiesLocations()
		}
		let throttleTimeout: ReturnType<typeof setTimeout> | null = null
		const throttledHandleScroll = () => {
			if (throttleTimeout) return
			throttleTimeout = setTimeout(() => {
				const container = groupRef.current?.querySelector("ol")
				if (!container) return

				const isBottom =
					container.scrollHeight - container.scrollTop <=
					container.clientHeight + SCROLL_THRESHOLD

				if (
					isBottom &&
					!isFetchingRef.current &&
					pageInfo.current?.next_cursor
				) {
					getActivitiesLocations()
				}

				throttleTimeout = null
			}, 300)
		}
		const container = groupRef.current?.querySelector("ol")
		if (container) {
			container.addEventListener("scroll", throttledHandleScroll)
			return () => {
				container.removeEventListener("scroll", throttledHandleScroll)
				if (throttleTimeout) clearTimeout(throttleTimeout)
			}
		}
	}, [getActivitiesLocations, params])

	React.useImperativeHandle(ref, () => ({
		getUnselectedIds: function () {
			return selectedItmes
		},
		isItemZero: !items.length && !status.isLoading,
	}))
	useEffect(() => {
		setSelectedItmes([])
	}, [isSelectAll])
	return (
		<Stack
			padding="4"
			borderWidth="1"
			borderColor="warning"
			rounded="lg"
			marginBottom="4"
			ref={groupRef}
		>
			{status.error ? (
				<Box
					display="flex"
					alignItems="center"
					gap={"3"}
					padding={"3"}
					borderColor={"danger"}
					borderRadius="md"
				>
					<Box as="span" flex="1" fontWeight="semibold">
						{status.error}
					</Box>
					<Box
						as="button"
						paddingX={"3"}
						paddingY={"1"}
						color="white"
						borderRadius="md"
						fontWeight="semibold"
						onClick={getActivitiesLocations}
					>
						Refresh
					</Box>
				</Box>
			) : null}
			<Box
				style={{
					height: "400px",
				}}
				overflowY="auto"
				as="ol"
				display={!items.length && !status.isLoading ? "flex" : "block"}
				flexDirection={"col"}
				justifyContent={"center"}
				alignItems={"center"}
			>
				{!items.length && !status.isLoading ? (
					<>
						<Box as="p">
							Please choose right filter or search option to disable any
							activities
						</Box>
						<Button onClick={openFilter} type="button">
							Choose Right Filter
						</Button>
					</>
				) : (
					items.map(({ id, name, ticket_types }) => (
						<Box
							as="li"
							key={id}
							padding="4"
							borderWidth="1"
							rounded="lg"
							marginBottom="4"
							bgColor="default"
						>
							<Stack gap="2">
								<Box marginBottom="2">
									<Inline cursor="pointer">
										<CheckboxInput
											id={name + id}
											checked={
												isSelectAll
													? !selectedItmes.includes(id)
													: selectedItmes.includes(id)
											}
											onChange={(e) => {
												const checked =
													(e.currentTarget.checked && isSelectAll) ||
													(!e.currentTarget.checked && !isSelectAll)
												if (checked) {
													setSelectedItmes((ids) =>
														ids.filter((singleId) => singleId !== id)
													)
													decreaseIncrease(
														isSelectAll ? "increase" : "decrease"
													)
												} else {
													setSelectedItmes((ids) => [...ids, id])
													decreaseIncrease(
														!isSelectAll ? "increase" : "decrease"
													)
												}
											}}
										/>
										<Box htmlFor={name + id} as="label">
											{name}
										</Box>
									</Inline>
								</Box>
								{ticket_types?.length ? (
									<Inline gap="2" flexWrap="wrap">
										{ticket_types?.map((s) => (
											<Box
												key={s.id}
												display="inlineBlock"
												paddingX="2"
												borderWidth="1"
												rounded="lg"
												bgColor="subtle"
											>
												{s.display_name}
												{s.short_name ? (
													<Box as="span" marginLeft="1" color="muted">
														[[{s.short_name}]]
													</Box>
												) : null}
											</Box>
										))}
									</Inline>
								) : null}
							</Stack>
						</Box>
					))
				)}
				{status.isLoading ? (
					<Inline justifyContent={"center"} gap={"2"} alignItems={"center"}>
						<Spinner /> Loading more activities...
					</Inline>
				) : null}
			</Box>
		</Stack>
	)
}
const InfiniteScroll = React.forwardRef<RefHandle, TInfiniteScrollBase>(
	InfiniteScrollBase
)

type TBatch = {
	id: string
	name: string
	totalJobs: number
	pendingJobs: number
	processedJobs: number
	progress: number
	failedJobs: number
	createdAt: string
	cancelledAt: string | null
	finishedAt: string | null
}

function Batch({
	children,
	onFinish,
}: {
	onFinish: () => void
	children: (props: {
		init: (batch: TBatch) => void
		xhr: XHRInstance
		inProgress: boolean
	}) => React.ReactNode
}) {
	const [batch, setBatch] = useState<null | TBatch>(null)
	const xhr = useXHR()
	const { data: currentBatch } = useSWR(
		batch ? `/batch/${batch.id}` : null,
		() =>
			xhr
				.get<{ data: TBatch }>(`batches/${batch?.id}`)
				.then((resp) => resp.data.data)
				.then((batch) => {
					if (batch.finishedAt) {
						onFinish()
					}
					return batch
				}),
		{
			refreshInterval: 1000,
		}
	)
	return (
		<>
			{children({
				xhr,
				init: (batch) => setBatch(batch),
				inProgress: Boolean(currentBatch && !currentBatch.finishedAt),
			})}
		</>
	)
}
